import * as am5 from '@amcharts/amcharts5';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';
import * as am5xy from '@amcharts/amcharts5/xy';
import { Injectable } from '@angular/core';
import { UUID } from 'crypto';
import {
  AmChartInput,
  AmChartLine,
  AmLineChartItem,
  XYChartSeriesType,
} from '../models/timeline-chart-input.interface';

@Injectable({
  providedIn: 'root',
})
export class AmChartService {
  private readonly strokeSettingsKey = 'strokeSettings';
  private readonly defaultStrokeSettings: Partial<am5.IGraphicsSettings> = {
    strokeWidth: 2,
    lineJoin: 'round',
  };

  seriesFactories: Record<XYChartSeriesType, any> = {
    line: am5xy.LineSeries,
    'smoothed-line': am5xy.SmoothedXLineSeries,
    step: am5xy.StepLineSeries,
  };

  /**
   * Creates an AmCharts line chart with the provided data, date range, and tooltip behavior.
   *
   * @param amChartInput - Chart configuration and data.
   * @param startTime - The start date of the X-axis range.
   * @param endTime - The end date of the X-axis range.
   * @param height - am chart height in pixels
   * @param yAxisMax - Optional fixed top of the Y-axis. When omitted, amCharts
   *   auto-derives the maximum from the data.
   * @returns An object containing:
   *   - `root`: The AmCharts root instance for manual disposal.
   *   - `seriesMap`: A map of series keyed by their `line.key` for later access.
   */
  createAmChart(
    amChartInput: AmChartInput,
    startTime: Date,
    endTime: Date,
    chartUuid: UUID,
    height: number,
    yAxisMax?: number
  ): { root: am5.Root; seriesMap: Map<string, am5xy.XYSeries[]> } {
    const root = this.initChartRoot(chartUuid);
    const seriesMap = new Map<string, am5xy.XYSeries[]>();

    const chart = this.createChartContainer(root, height);
    const xAxis = this.createXAxis(root, chart, startTime, endTime);
    const yAxis = this.createYAxis(root, chart, yAxisMax);
    this.createCursor(root, chart, xAxis);

    this.splitByMissingDates(amChartInput.data).forEach((data) => {
      amChartInput.lines.forEach((line: AmChartLine) => {
        const series = this.createSeries(
          root,
          chart,
          xAxis,
          yAxis,
          this.prepareData(data),
          line,
          amChartInput.seriesType
        );
        if (seriesMap.has(line.key)) {
          seriesMap.get(line.key)?.push(series);
        } else {
          seriesMap.set(line.key, [series]);
        }
      });
    });
    chart.appear(1000, 100);
    return { root, seriesMap };
  }

  /**
 * Updates an existing amCharts XYChart with new data and axis ranges.
 *
 * This function performs the following steps:
 *  1. Retrieves the existing XYChart instance from the provided root.
 *  2. Retrieves the first X (DateAxis) and Y (ValueAxis) axes from the chart.
 *  5. Disposes all existing series on the chart to remove old data.
 *  4. Updates the X-axis min/max values to the provided startTime and endTime,
 *     and re-pins the Y-axis to the provided yAxisMax.
 *  5. Recreates series based on the provided AmChartInput data and series definitions,
 *     and returns a map of series keyed by their `line.key`.

 * @param root - The amCharts Root instance containing the chart.
 * @param amChartInput - Object containing the data and series definitions to render.
 * @param startTime - Minimum visible date/time on the X-axis.
 * @param endTime - Maximum visible date/time on the X-axis.
 * @param yAxisMax - Optional fixed top of the Y-axis. When omitted, amCharts
 *   auto-derives the maximum from the data.
 *
 * @returns A Map where each key corresponds to a series key from `amChartInput.lines`,
 *          and the value is an array of XYSeries instances corresponding to that key.
 */
  updateAmChart(
    root: am5.Root,
    amChartInput: AmChartInput,
    startTime: Date,
    endTime: Date,
    yAxisMax?: number
  ): Map<string, am5xy.XYSeries[]> {
    //----------------------------
    // 1. Get the chart instance
    //----------------------------
    let chart: am5xy.XYChart | undefined;

    root.container.children.each((child) => {
      if (child instanceof am5xy.XYChart) {
        chart = child;
      }
    });
    if (!chart) {
      console.error('Chart not found on root.');
      return new Map();
    }

    //----------------------------
    // 2. Get the axes
    //----------------------------
    const xAxis = chart.xAxes.getIndex(0) as am5xy.DateAxis<any>;
    const yAxis = chart.yAxes.getIndex(0) as am5xy.ValueAxis<any>;

    if (!xAxis || !yAxis) {
      console.error('Axis not found on chart.');
      return new Map();
    }

    //----------------------------
    // 3. Clear old series
    //----------------------------
    chart.series.clear();
    xAxis.data.clear();
    yAxis.data.clear();

    //----------------------------
    // 4. Update axis range
    //----------------------------
    xAxis.setAll({
      min: startTime.getTime(),
      max: endTime.getTime(),
      strictMinMax: true,
    });

    yAxis.setAll({
      min: 0,
      max: yAxisMax,
      strictMinMax: yAxisMax !== undefined,
    });

    //----------------------------
    // 5. Recreate all series
    //----------------------------
    const newSeriesMap = new Map<string, am5xy.XYSeries[]>();

    this.splitByMissingDates(amChartInput.data).forEach((dataChunk) => {
      amChartInput.lines.forEach((line) => {
        const series = this.createSeries(
          root,
          chart!,
          xAxis,
          yAxis,
          this.prepareData(dataChunk),
          line,
          amChartInput.seriesType
        );

        if (!newSeriesMap.has(line.key)) {
          newSeriesMap.set(line.key, []);
        }
        newSeriesMap.get(line.key)!.push(series);
      });
    });

    return newSeriesMap;
  }

  /**
   * Initializes the AmCharts root and applies the animated theme.
   * Also removes the default amCharts logo.
   *
   * @returns The initialized AmCharts root instance.
   */
  private initChartRoot(chartUuid: UUID): am5.Root {
    const root = am5.Root.new(chartUuid);
    root.setThemes([am5themes_Animated.new(root)]);
    root['_logo']!.dispose();
    return root;
  }

  /**
   * Prepares and sorts raw chart data for AmCharts.
   * Converts date strings to timestamps for proper axis scaling.
   *
   * @param data - Array of AmLineChartItems to transform.
   * @returns Transformed and sorted data array for chart consumption.
   */
  private prepareData(data: AmLineChartItem[]): any[] {
    return data
      .map((item: AmLineChartItem) => ({
        ...item.values,
        position: item.position,
        date: new Date(item.date).getTime(),
      }))
      .sort((a, b) => a.date - b.date);
  }

  /**
   * Creates the main chart container with basic layout settings.
   *
   * @param root - AmCharts root instance.
   * @returns The configured XYChart container.
   */
  private createChartContainer(root: am5.Root, height: number): am5xy.XYChart {
    const chart = am5xy.XYChart.new(root, {
      panY: false,
      layout: root.verticalLayout,
      height,
      paddingRight: 0,
      paddingTop: 0,
      paddingBottom: 0,
      paddingLeft: -10,
    });

    chart.zoomOutButton.set('visible', false);
    chart.zoomOutButton.set('forceHidden', true);

    return root.container.children.push(chart);
  }

  /**
   * Creates a DateAxis (X-axis) with min/max bounds and hidden labels.
   *
   * @param root - AmCharts root instance.
   * @param chart - The parent XYChart container.
   * @param startTime - Minimum visible time on the axis.
   * @param endTime - Maximum visible time on the axis.
   * @returns The configured DateAxis.
   */
  private createXAxis(
    root: am5.Root,
    chart: am5xy.XYChart,
    startTime: Date,
    endTime: Date
  ): am5xy.DateAxis<am5xy.AxisRenderer> {
    const xAxis = chart.xAxes.push(
      am5xy.DateAxis.new(root, {
        groupData: false,
        baseInterval: { timeUnit: 'second', count: 1 },
        renderer: am5xy.AxisRendererX.new(root, {
          minGridDistance: 10,
          strokeOpacity: 0,
        }),
        width: am5.percent(100),
        min: startTime.getTime(),
        max: endTime.getTime(),
      })
    );

    xAxis.get('renderer').grid.template.setAll({ forceHidden: true });
    xAxis.get('renderer').labels.template.setAll({ visible: false });
    return xAxis;
  }

  /**
   * Creates a ValueAxis (Y-axis) with hidden labels.
   *
   * @param root - AmCharts root instance.
   * @param chart - The parent XYChart container.
   * @param max - Optional fixed top of the axis. When provided the axis is pinned
   *   to `[0, max]` so an external label column can describe the real scale;
   *   when omitted amCharts auto-derives the maximum from the data.
   * @returns The configured ValueAxis.
   */
  private createYAxis(
    root: am5.Root,
    chart: am5xy.XYChart,
    max?: number
  ): am5xy.ValueAxis<am5xy.AxisRenderer> {
    const yAxis = chart.yAxes.push(
      am5xy.ValueAxis.new(root, {
        renderer: am5xy.AxisRendererY.new(root, {
          minGridDistance: 10,
          strokeOpacity: 0,
          marginBottom: 0,
          marginTop: 0,
        }),
        min: 0,
        max,
        strictMinMax: max !== undefined,
      })
    );

    yAxis.get('renderer').labels.template.setAll({ visible: false });
    return yAxis;
  }

  /**
   * Adds a cursor to the chart for interaction without showing guide lines.
   *
   * @param root - AmCharts root instance.
   * @param chart - The parent XYChart container.
   * @param xAxis - The chart's X-axis for cursor binding.
   */
  private createCursor(
    root: am5.Root,
    chart: am5xy.XYChart,
    xAxis: am5xy.DateAxis<am5xy.AxisRenderer>
  ): void {
    const cursor = chart.set(
      'cursor',
      am5xy.XYCursor.new(root, { xAxis, behavior: 'none' })
    );
    cursor.lineY.set('visible', false);
    cursor.lineX.set('visible', false);
  }

  /**
   * Creates a single line series with gradient fill and optional tooltip behavior.
   *
   * @param root - AmCharts root instance.
   * @param chart - Parent XYChart container.
   * @param xAxis - DateAxis instance.
   * @param yAxis - ValueAxis instance.
   * @param data - Pre-processed chart data.
   * @param line - Line configuration object.
   * @returns The created XYSeries.
   */
  private createSeries(
    root: am5.Root,
    chart: am5xy.XYChart,
    xAxis: am5xy.DateAxis<am5xy.AxisRenderer>,
    yAxis: am5xy.ValueAxis<am5xy.AxisRenderer>,
    data: any[],
    line: AmChartLine,
    seriesType: XYChartSeriesType = 'line'
  ): am5xy.XYSeries {
    const settings: am5xy.ILineSeriesSettings = {
      name: line.title,
      xAxis,
      yAxis,
      valueYField: line.key,
      valueXField: 'date',
      locationX: 0,
      stroke: am5.color(line.color),
      fill: am5.color(line.color),
      showTooltipOn: 'click',
      snapTooltip: true,
      ...(line.disabled
        ? {}
        : {
            tooltip: am5.Tooltip.new(root, {
              pointerOrientation: 'horizontal',
              visible: false,
              forceHidden: true,
            }),
          }),
    };

    const factory = this.seriesFactories[seriesType];

    const series = chart.series.push(factory.new(root, settings));
    series.strokes.template.setAll(this.defaultStrokeSettings);

    const seriesData =
      seriesType === 'step'
        ? this.prepareStepLineSeriesData(data, line.key)
        : data;

    series.data.setAll(seriesData);

    const gradient = am5.LinearGradient.new(root, {
      target: chart.plotContainer,
      stops: [line.color, '#FFFFFF'].map((c, i) => ({
        color: am5.color(c),
        offset: i,
      })),
    });

    series.fills.template.setAll({
      fillGradient: gradient,
      fillOpacity: line.fillOpacity ?? 0.5,
      visible: true,
    });

    series.states.create('highlighted', { opacity: 1 });
    series.states.create('faded', { opacity: 0.2 });
    series.states.create('hidden', { opacity: 0 });

    if (line.disabled) {
      series.states.applyAnimate('hidden');
    }

    return series;
  }

  /**
   * Finds the chart item closest to a target date.
   * Returns null if the targetDate is outside the dataset's date range.
   */
  getClosestAmChartItemByDate(
    targetDate: Date,
    chartInput: AmChartInput
  ): AmLineChartItem | null {
    if (!this.isDateWithinDataRange(targetDate, chartInput.data)) {
      return null;
    } else if (chartInput.seriesType === 'step') {
      return this.getCurrentOrPreviousAmChartItem(targetDate, chartInput);
    }
    return this.findClosestItem(targetDate, chartInput.data);
  }

  /**
   * Finds the chart item by date: the date on or before the given target date,
   * used for aligning tooltip data in a step line series.
   */
  private getCurrentOrPreviousAmChartItem(
    targetDate: Date,
    chartInput: AmChartInput
  ): AmLineChartItem | null {
    return chartInput.data
      .filter((item) => new Date(item.date) <= targetDate)
      .reduce((max, curr) =>
        !max || new Date(curr.date) >= new Date(max.date) ? curr : max
      );
  }

  /**
   * Prepares a dataset for rendering a step line series chart with visual breakpoints to match the expected design.
   * Applies different stroke settings based on whether the line value is zero or non-zero.
   * If a zero value is followed by a non-zero value, inserts a synthetic breakpoint just before the transition.
   * This ensures the stroke is hidden for zero values, while displaying the stroke for the "riser" just before a non-zero value.
   */
  private prepareStepLineSeriesData(data: any[], lineKey: string): any[] {
    const upcomingNonZeroPoints: typeof data = [];
    const strokeSettings = {
      [this.strokeSettingsKey]: this.defaultStrokeSettings,
    };
    const styledData = data.map((point, i, points) => {
      if (point[lineKey] === 0) {
        const nextPoint = points[i + 1];
        if (nextPoint?.[lineKey] > 0) {
          upcomingNonZeroPoints.push(nextPoint);
        }
        return { ...point, [this.strokeSettingsKey]: { strokeOpacity: 0 } };
      }

      return { ...point, ...strokeSettings };
    });

    const newBreakpointPoints = upcomingNonZeroPoints.map((point) => ({
      ...point,
      date: point.date - 1,
      [lineKey]: 0,
      ...strokeSettings,
    }));

    return [...styledData, ...newBreakpointPoints].sort(
      (a, b) => a.date - b.date
    );
  }

  /**
   * Checks if the given date is within the first and last data point's dates.
   */
  private isDateWithinDataRange(
    targetDate: Date,
    data: AmLineChartItem[]
  ): boolean {
    if (data.length === 0) return false;
    const firstDate = new Date(data[0].date);
    const lastDate = new Date(data[data.length - 1].date);
    return targetDate >= firstDate && targetDate <= lastDate;
  }

  /**
   * Finds the data item whose date is closest to the target date.
   */
  private findClosestItem(
    targetDate: Date,
    data: AmLineChartItem[]
  ): AmLineChartItem {
    return data.reduce((closest, current) => {
      const currentDiff = Math.abs(
        new Date(current.date).getTime() - targetDate.getTime()
      );
      const closestDiff = Math.abs(
        new Date(closest.date).getTime() - targetDate.getTime()
      );
      return currentDiff < closestDiff ? current : closest;
    });
  }

  /**
   * Splits a sorted array of AmLineChartItem into multiple subarrays
   * whenever there is a missing day between consecutive items.
   *
   * @param data - Array of AmLineChartItem, should be sorted by date ascending
   * @returns An array of AmLineChartItem arrays, split by missing dates
   */
  splitByMissingDates(data: AmLineChartItem[]): AmLineChartItem[][] {
    if (!data || data.length === 0) return [];

    // Sort by date ascending to ensure proper splitting
    const sorted = [...data].sort(
      (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
    );

    const result: AmLineChartItem[][] = [];
    let currentChunk: AmLineChartItem[] = [sorted[0]];

    for (let i = 1; i < sorted.length; i++) {
      const prevDate = new Date(sorted[i - 1].date);
      const currDate = new Date(sorted[i].date);

      const diffInDays = Math.floor(
        (currDate.getTime() - prevDate.getTime()) / (1000 * 60 * 60 * 24)
      );

      if (diffInDays > 1) {
        // Gap detected → start a new chunk
        result.push(currentChunk);
        currentChunk = [sorted[i]];
      } else {
        // Consecutive day → add to current chunk
        currentChunk.push(sorted[i]);
      }
    }

    // Push the last chunk
    if (currentChunk.length > 0) {
      result.push(currentChunk);
    }

    return result;
  }
}
